'use client';
import React, { useState } from 'react';
import { faLongArrowRight } from '@fortawesome/pro-light-svg-icons/faLongArrowRight';

import styled from '@benzinga/themetron';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Icon } from '@benzinga/core-ui';

import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { SessionContext } from '@benzinga/session-context';
import { emailRegEx } from '../../utils/regexes';

import { ContentManager } from '@benzinga/content-manager';
import { TrackingManager } from '@benzinga/tracking-manager';

export interface BeehiivEmmailCaptureProps {
  title?: string;
  pub_id?: string;
  description?: string;
}

export const BeehiivEmailCapture = ({ description, pub_id = '', title }: BeehiivEmmailCaptureProps) => {
  const session = React.useContext(SessionContext);
  const [success, setSuccess] = React.useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>('');
  const [responseSuccessMessage, setResponseSuccessMessage] = React.useState<string>('');

  const handleSubmit = () => {
    setError(null);
    if (!emailRegEx.test(email)) {
      setError('Invalid email.');
      return;
    }
    const params = new URL(`${window.location}`).searchParams;
    const beehiiv_pub_id = pub_id.startsWith('pub_') ? pub_id : 'pub_' + pub_id;

    session
      .getManager(ContentManager)
      .subscribeToNewsletter({
        email: email,
        pub_id: beehiiv_pub_id,
        url: window.location.href,
        utm_campaign: params.get('utm_campaign'),
        utm_medium: params.get('utm_medium'),
        utm_source: params.get('utm_source'),
      })
      .then(res => {
        if (res.ok) {
          const { message, status } = res.ok;
          if (status === 'success') {
            setSuccess(true);
            setResponseSuccessMessage(message);
            session.getManager(TrackingManager).trackFormEvent('submit', 'beehiiv-form-sign-up', {
              email: email,
              form_id: pub_id,
              form_type: 'Beehiiv Form',
            });
          } else {
            setError('Something went wrong.');
            console.log('Something went wrong.');
          }
        } else {
          setError('Something went wrong!!');
          console.log('Something went wrong!!');
        }
      })
      .catch(err => {
        console.log('Something went wrong!');
        console.log(err);
      });
  };

  return (
    <BeehiivEmailContainer className={`beehiiv-email-capture-wrapper`}>
      <div>
        {title && (
          <div className="title">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
          </div>
        )}
        {description && (
          <div className="subtitle">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />
          </div>
        )}

        <div className="success-message-container">
          {success && (
            <div className="success-message-text">
              <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(responseSuccessMessage) }} />
            </div>
          )}
          {!success && pub_id && (
            <div className="email-capture">
              <Icon className={'text-gray hubspot-email-icon'} icon={faEnvelope} />
              <input
                className="hubspot-input w-full text-sm px-2 leading-none placeholder-gray-600 bg-transparent border-none ring-0"
                onChange={e => {
                  setError(null);
                  setEmail(e.target.value);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                }}
                placeholder={'Email'}
                type="email"
                value={email}
              />
              <button
                aria-label="Click to submit email"
                className={
                  'focus:outline-none py-1.5 px-2 inline-flex items-center justify-center bg-yellow-500 text-white rounded hubspot-form-button'
                }
                onClick={handleSubmit}
                type="button"
              >
                <Icon className={'text-white'} icon={faLongArrowRight} />
              </button>
            </div>
          )}
        </div>
        {error && (
          <div className="p-1">
            <span className="text-red-500 bold">{error}</span>
          </div>
        )}
      </div>
    </BeehiivEmailContainer>
  );
};

const BeehiivEmailContainer = styled.div`
  &.beehiiv-email-capture-wrapper {
    display: flex;
    flex-direction: column;
    border-width: 1px;
    background-color: #ffffff;
    box-shadow: ${({ theme }) => theme.shadow.md};
    border-color: rgb(156, 163, 175);
    border-radius: 0.375rem;

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.lg};
    }

    > div:last-of-type {
      display: flex;
      flex-direction: column;
      padding: 1rem;

      > div {
        margin-bottom: 0.5rem;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }

    .title {
      font-size: 1.125rem;
      line-height: 1.75rem;
      font-weight: 700;
      border-color: ${({ theme }) => theme.colorPalette.blue600};
      text-transform: uppercase;
    }

    .subtitle {
      font-size: 0.875rem;
      line-height: 1.25rem;
      margin-top: 5px;
      margin-bottom: 15px;
    }

    .success-message-container {
      display: flex;
      justify-content: center;

      .success-message-text {
        color: ${({ theme }) => theme.colorPalette.green400};
        font-size: 14px;
      }

      > div {
        font-weight: 700;
      }
    }
    .email-capture {
      padding: 0.25rem 0.25rem 0.25rem 0.625rem;
      display: inline-flex;
      background-color: rgba(206, 221, 242, 0.1);
      border: 1px solid rgba(184, 203, 229, 0.7);
      border-radius: 0.25rem;
      align-items: center;
      width: 100%;
    }
  }
`;
